import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import config from '../../config';
import RestEntityAPI from './restEntityAPI';
import logger from '../logger';
import fetch, { toQueryString } from '../fetch';

const options = {
  entityName: 'job',
  endpoint: jobId => (jobId ? `${config.apiUrl}/v1/job-listings/${jobId}` : `${config.apiUrl}/v1/job-listings`),
  searchEndpoint: `${config.apiUrl}/v1/jobs/search`,
};

const JobsAPI = () => {
  const recommendedJobsEndpoint = `${config.apiUrl}/v1/jobs/recommended-jobs`;
  const cachedJobsEndpoint = `${config.apiUrl}/v1/jobs/cached-search`;
  const presetSearchEndpoint = slug => `${config.apiUrl}/v1/preset-job-search-query-templates/${slug}/jobs`;
  const candidatesEndpoint = `${config.apiUrl}/v1/candidates`;
  const parentAPI = RestEntityAPI(options);

  const searchEntities = async (query, fetchOptions = {}) => {
    const response = await parentAPI.searchEntities(query, fetchOptions);
    const jobListings = get(response, 'data.job_listings', []);
    const companyResults = get(response, 'data.company_results', []);
    const jobSourceResults = get(response, 'data.job_source_results', []);
    const isCachedSearch = get(response, 'data.cached_search', false);
    const isFallbackSearch = get(response, 'data.fallback_search', false);

    return { jobListings, companyResults, jobSourceResults, isCachedSearch, isFallbackSearch };
  };

  const searchPresetEntities = async (slug, query, fetchOptions = {}) => {
    try {
      const response = await fetch(`${presetSearchEndpoint(slug)}${parentAPI.toQueryString(query)}`, fetchOptions);

      return response.data;
    } catch (error) {
      logger.logDebug(`Encountered error while loading ${options.entityName}s`, error);
      throw error;
    }
  };

  const searchCachedEntities = async (query, options) => {
    try {
      const response = await fetch(`${cachedJobsEndpoint}${parentAPI.toQueryString(query)}`, options);

      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while loading ${options.entityName}s`, error);
      throw error;
    }
  };

  const getRecommendedJobs = async (query, explicitTitle = null, fetchOptions = {}) => {
    const recommendedQuery = !isEmpty(explicitTitle) ? { ...query, query: explicitTitle } : query;

    try {
      const response = await fetch(`${recommendedJobsEndpoint}/${parentAPI.toQueryString(recommendedQuery)}`, {
        method: 'GET',
        ...fetchOptions,
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while loading ${options.entityName}s`, error);
      throw error;
    }
  };

  const getListingInfo = async (id, impressionKey = null) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint, id)}/info${parentAPI.toQueryString(impressionKey)}`, {
        method: 'GET',
      });

      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while fetching job listing info with ${options.entityName}`, error);
      throw error;
    }
  };

  const getJob = async ({ jobId, ctx }) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job/${jobId}`;
    try {
      return await fetch(getUrl, { method: 'GET', ctx: ctx || {} });
    } catch (error) {
      throw error;
    }
  };

  const getSubOrganizationSuggestions = async () => {
    const getUrl = `${config.apiUrl}/v1/job-manage/sub-org/list`;
    try {
      return await fetch(getUrl, { method: 'GET' });
    } catch (error) {
      throw error;
    }
  };

  const getJobs = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job-list${toQueryString(query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      logger.logDebug('Failed to search jobs', error);
      throw error;
    }
  };

  const createJob = async (data) => {
    try {
      const response = await fetch(`${config.apiUrl}/v1/job-manage/job`, { method: 'POST', body: data });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateJob = async ({ jobId, data }) => {
    const body = { data: { id: jobId, ...data } };
    try {
      return await fetch(`${config.apiUrl}/v1/job-manage/job`, {
        method: 'PATCH',
        body,
      });
    } catch (error) {
      logger.logDebug(`Failed to update for ${jobId}`, error);
      throw error;
    }
  };

  const getJobGroups = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job-group-list${toQueryString(query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      throw error;
    }
  };

  const getJobGroup = async (jobGroupId, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job-group/${jobGroupId}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      throw error;
    }
  };

  const updateJobGroups = async ({ jobGroupId, data }) => {
    const body = { data: { id: jobGroupId, ...data } };
    try {
      return await fetch(`${config.apiUrl}/v1/job-manage/job-group`, {
        method: 'PATCH',
        body,
      });
    } catch (error) {
      logger.logDebug(`Failed to update for ${jobGroupId}`, error);
      throw error;
    }
  };

  const getJobTemplates = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job/templates${toQueryString(query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
    try {
      return await fetch(getUrl, { method: 'GET', headers });
    } catch (error) {
      throw error;
    }
  };

  const createJobGroup = async (data, profileKey) => {
    try {
      const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};
      const response = await fetch(`${config.apiUrl}/v1/job-manage/job-group`, { method: 'POST', headers, body: data });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrgJobsStates = async (query, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/job/org/states${toQueryString(query)}`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to fetch jobs states for organization ${query.org_id}`, error);
      throw error;
    }
  };

  const getOrgUrlParameters = async (orgId, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/organization/${orgId}/extra_params`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      return await fetch(getUrl, {
        method: 'GET',
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to get URL parameters of organization ${orgId}`, error);
      throw error;
    }
  };

  const updateOrgUrlParameters = async (orgId, params, profileKey) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/organization/${orgId}/extra_params`;
    const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey } : {};

    try {
      return await fetch(getUrl, {
        method: 'PATCH',
        body: { url_params: params },
        headers,
      });
    } catch (error) {
      logger.logDebug(`Failed to update URL parameters of organization ${orgId}`, error);
      throw error;
    }
  };

  const getCandidates = async (options = {}) => {
    const { requestBody } = options;

    try {
      const response = await fetch(`${candidatesEndpoint}/filter`, {
        method: 'POST',
        body: {
          data: {
            ...requestBody,
          },
        },
      });
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to fetch candidates for job ${requestBody?.job_id}`, error);
      throw error;
    }
  };

  const getCandidateDetails = async (candidateId) => {
    try {
      const response = await fetch(`${candidatesEndpoint}/${candidateId}/details`);
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to fetch candidate details of candidate id ${candidateId}`, error);
      throw error;
    }
  };

  const hideCandidates = async (candiatesIdList) => {
    try {
      await fetch(`${candidatesEndpoint}/set-visibility`, {
        method: 'PUT',
        body: {
          data: {
            candidate_ids: candiatesIdList,
            is_visible: false,
          },
        },
      });
    } catch (error) {
      logger.logDebug('Failed to hide candidates for job', error);
      throw error;
    }
  };

  const unhideCandidates = async (candiatesIdList) => {
    try {
      await fetch(`${candidatesEndpoint}/set-visibility`, {
        method: 'PUT',
        body: {
          data: {
            candidate_ids: candiatesIdList,
            is_visible: true,
          },
        },
      });
    } catch (error) {
      logger.logDebug('Failed to hide candidates for job', error);
      throw error;
    }
  };

  const getCandidatesNotes = async (id = '', options = {}) => {
    try {
      const response = await fetch(`${candidatesEndpoint}/${id}/notes${toQueryString(options.query)}`);
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to fetch notes for candidates ${id}`, error);
      throw error;
    }
  };

  const createCandidatesNote = async (id = '', content = '') => {
    try {
      const response = await fetch(`${candidatesEndpoint}/${id}/notes`, {
        method: 'POST',
        body: {
          data: {
            content,
          },
        },
      });
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to create notes for candidate ${id}`, error);
      throw error;
    }
  };

  const updateCandidatesNote = async (candidateId, noteId, noteContent) => {
    try {
      await fetch(`${candidatesEndpoint}/${candidateId}/notes/${noteId}`, {
        method: 'PATCH',
        body: { data: { content: noteContent } } });
    } catch (error) {
      logger.logDebug(`Failed to update notes for candidate ${candidateId}`, error);
      throw error;
    }
  };

  const rateCandidate = async (candidateId, data) => {
    try {
      const response = await fetch(`${candidatesEndpoint}/${candidateId}/rate`, {
        method: 'PUT',
        body: { data },
      });
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to rate candidate ${candidateId}`, error);
      throw error;
    }
  };

  const bulkRateCandidates = async (data) => {
    try {
      const response = await fetch(`${candidatesEndpoint}/rate`, {
        method: 'PUT',
        body: { data },
      });
      return response.data;
    } catch (error) {
      logger.logDebug('Failed to bulk rate candidates', error);
      throw error;
    }
  };

  const getEmailSuggestions = async ({ organizationId }) => {
    try {
      return await fetch(`${config.apiUrl}/v1/orgs/${organizationId}/profiles`, {
        method: 'GET',
      });
    } catch (error) {
      logger.logDebug(`Failed to fetch email suggestions for organization ${organizationId}`, error);
      throw error;
    }
  };

  const getSubOrgJobCounts = async (query) => {
    const baseUrl = `${config.apiUrl}/v1/job-manage/sub-org/counts?${query}`;
    try {
      const response = await fetch(baseUrl, { method: 'GET' });
      return response?.data;
    } catch (error) {
      throw error;
    }
  };

  const getJobSummary = async (organizationId) => {
    const baseUrl = `${config.apiUrl}/v1/job-manage/organization/${organizationId}/job_summary`;
    try {
      const response = await fetch(baseUrl, { method: 'GET' });
      return response?.data;
    } catch (error) {
      throw error;
    }
  };

  const forwardCandidate = async (data) => {
    try {
      const response = await fetch(`${candidatesEndpoint}/forward`, {
        method: 'POST',
        body: { data },
      });
      return response.data;
    } catch (error) {
      logger.logDebug('Failed to bulk forward candidates', error);
      throw error;
    }
  };

  const getScreenerAnswers = async (candidateId = '') => {
    try {
      const response = await fetch(`${candidatesEndpoint}/${candidateId}/screener-answers`);
      return response.data;
    } catch (error) {
      logger.logDebug(`Failed to fetch candidate screener answers for candidate id ${candidateId}`, error);
      throw error;
    }
  };

  const getJobsInCSV = async (queryParams) => {
    const getUrl = `${config.apiUrl}/v1/job-manage/report${toQueryString(queryParams)}`;

    try {
      const response = await fetch(getUrl, {
        method: 'GET',
        fullResponse: true,
      });

      return await response.text();
    } catch (error) {
      throw error;
    }
  };

  const getJobsCandidatesCount = async (organizationId, queryParams) => {
    const baseUrl = `${config.apiUrl}/v1/orgs`;
    const query = { context: 'JOB', ...queryParams };
    try {
      return await fetch(`${baseUrl}/${organizationId}/candidates/counts${toQueryString(query)}`, {
        method: 'GET',
      });
    } catch (error) {
      throw error;
    }
  };

  const getJobGroupById = async (id) => {
    try {
      const response = await fetch(`${config.apiUrl}/v1/job-manage/job-group/${id}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      return error;
    }
  };

  const getJobReportingInCSV = async (body = {}) => {
    const getUrl = `${config.apiUrl}/v1/reporting/export/Report.csv`;

    try {
      const response = await fetch(getUrl, {
        method: 'POST',
        fullResponse: true,
        body,
      });

      return await response.text();
    } catch (error) {
      throw error;
    }
  };

  return merge({}, { ...parentAPI }, {
    getScreenerAnswers,
    searchEntities,
    searchPresetEntities,
    searchCachedEntities,
    getRecommendedJobs,
    getListingInfo,
    getJob,
    getJobs,
    createJob,
    updateJob,
    getJobGroups,
    getJobTemplates,
    createJobGroup,
    updateJobGroups,
    getOrgJobsStates,
    getCandidates,
    getSubOrgJobCounts,
    getJobSummary,
    getCandidateDetails,
    getCandidatesNotes,
    createCandidatesNote,
    updateCandidatesNote,
    rateCandidate,
    hideCandidates,
    unhideCandidates,
    bulkRateCandidates,
    getSubOrganizationSuggestions,
    getEmailSuggestions,
    forwardCandidate,
    updateOrgUrlParameters,
    getOrgUrlParameters,
    getJobsInCSV,
    getJobsCandidatesCount,
    getJobGroup,
    getJobGroupById,
    getJobReportingInCSV,
  });
};

export default JobsAPI;
